import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";

import type { AcqCatalogItem } from "../../api/acqService";
import {
  buildProductVocab,
  isKeywordRelevant,
  KEYWORD_IRRELEVANT_HINT,
  type ProductVocabInput,
} from "../../model/keywordRelevance";
import {
  COMMENT_NO_TOPIC_HINT,
  COMMENT_TOPIC_CLEARED_HINT,
  isCommentArchetype,
  TOPIC_WORDS_MAX,
} from "../../model/acqLabels";
import { PEER_ACCOUNTS_MAX, PEER_POSTS_MAX, parsePeerLines } from "../../model/peerTargets";
import { ChipInput } from "./ChipInput";

/** 评论原型那两栏(契约 §5);1011 起抖音 / 小红书评论线多三格竞品配置(后端给了 `peerTargets` 才带)。 */
export interface KeywordsDrawerTopic {
  topicWords: string[];
  intentPatterns: string[];
  peerAccounts?: string[];
  peerPosts?: string[];
  peerAccountPostsN?: number;
}

const PEER_POSTS_N_OPTIONS = [1, 2, 3, 4, 5] as const;

/** 抖音线叫「视频」,小红书线叫「笔记」。 */
function postNoun(type: string | undefined): string {
  return type === "xhs_note" ? "笔记" : "视频";
}
function platformName(type: string | undefined): string {
  return type === "xhs_note" ? "小红书" : "抖音";
}

/**
 * 改一种发现的关键词 / 地区。
 *
 * ★ 面上必须明写**「改过之后重新推断不会覆盖」**(契约 §12.10 的前端落点)。
 *   不说的话,用户改完词、过两天点了「重新推断」、发现词还在,会以为是没生效;
 *   反过来如果真被覆盖了而我们没说过这件事,他会以为自己改丢了。
 *   这句话把 `keywordsSource='user'` 这个后端字段翻译成了用户能预期的行为。
 */
export function KeywordsDrawer({
  item,
  products = [],
  open,
  saving,
  onOpenChange,
  onSave,
}: {
  item: AcqCatalogItem | null;
  /** 在售产品(判「新加的词和产品有没有关系」用);不给 / 空 = 判不了,不提示。 */
  products?: readonly ProductVocabInput[];
  open: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * 第三参只在**评论原型**时给(契约 §5):话题词 + 客户常问。非评论原型不给 ⇒ PUT body 与以前逐字相同。
   */
  onSave: (keywords: string[], regions: string[], topic?: KeywordsDrawerTopic) => void;
}) {
  const [keywords, setKeywords] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [topicWords, setTopicWords] = useState<string[]>([]);
  const [intentPatterns, setIntentPatterns] = useState<string[]>([]);
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [peerAccountsText, setPeerAccountsText] = useState("");
  const [peerPostsText, setPeerPostsText] = useState("");
  const [peerPostsN, setPeerPostsN] = useState(2);
  const comment = isCommentArchetype(item);
  // ★ 只有后端给了 `peerTargets`(抖音 / 小红书评论线)才摆竞品两栏;老后端 / 别的线 ⇒ 不摆、不发。
  const peers = comment ? (item?.peerTargets ?? null) : null;
  const peerAccounts = parsePeerLines(peerAccountsText);
  const peerPosts = parsePeerLines(peerPostsText);
  /** 老数据(1023 前 ai 最多给 8 个、编辑框不限)会超:不替他删,让他自己挑留哪几个。 */
  const topicOverflow = comment && topicWords.length > TOPIC_WORDS_MAX;
  /** 1027 测试机:原有话题词被存成空,下一轮静默改用行业默认词搜偏。删光时在保存前说出来。 */
  const topicCleared = comment && topicWords.length === 0 && (item?.topicWords?.length ?? 0) > 0;
  const peerOverflow =
    peers !== null &&
    (peerAccounts.length > PEER_ACCOUNTS_MAX || peerPosts.length > PEER_POSTS_MAX);

  // 每次换一种(或重新打开)都从那一行的当前值起步,不留上一种的残留。
  useEffect(() => {
    if (!item) return;
    setKeywords([...item.keywords]);
    setRegions([...item.regions]);
    setTopicWords([...(item.topicWords ?? [])]);
    setIntentPatterns([...(item.intentPatterns ?? [])]);
    setPatternsOpen(false);
    setPeerAccountsText((item.peerTargets?.accounts ?? []).map((t) => t.input).join("\n"));
    setPeerPostsText((item.peerTargets?.posts ?? []).map((t) => t.input).join("\n"));
    setPeerPostsN(item.peerTargets?.accountPostsN ?? 2);
  }, [item, open]);

  const removed = item ? item.keywords.filter((w) => !keywords.includes(w)) : [];
  const vocab = useMemo(
    () => buildProductVocab(products, item?.keywordsByProduct),
    [products, item]
  );
  // ★ 只看**用户新加**的词:原有的词是 AI 按产品推出来的,对它们说「和产品不相关」是自相矛盾。
  //   只提示不拦 —— 判据是纯字面,误判的代价只能是一行灰字。
  const unrelated = item
    ? keywords.filter((w) => !item.keywords.includes(w) && isKeywordRelevant(w, vocab) === false)
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{item ? `盯什么 · ${item.name}` : "盯什么"}</SheetTitle>
          <SheetDescription asChild>
            {comment ? (
              <div className="space-y-1 text-left">
                <p>系统会搜这些话题下评论多的视频，再读评论找问价的人。</p>
                <p className="font-semibold text-warning">
                  改过之后，「重新推断」不会再覆盖这一种的词。
                </p>
              </div>
            ) : (
              <div className="space-y-1 text-left">
                <p>不是你卖什么，是客户遇到麻烦时会怎么说。</p>
                {/* ★ 卡面按产品分组、这里编的却是一张扁平表 —— 这个落差必须在面上说清楚,
                  否则用户会以为"我加的词没进那个产品",然后去产品页找一个不存在的入口。
                  分组是 AI 推断的产物,采集读的始终是这张扁平表(契约 §13.3)。 */}
                <p>
                  卡上按产品分组是 AI
                  给的；你在这儿加的词进「通用」组，对每个产品都生效，搜索时排在最前面。
                </p>
                {/* ★ 0923 测试机:销帮租户(卖 GEO / 400)把微信搜一搜的词整批换成「商标注册」一类,
                  156 篇全判 0 —— 精判是拿**在售产品**去判「是不是客户」的,这件事面上从没说过。 */}
                <p>
                  判卡时按你在售的产品来判：和在售产品无关的词，拉回来的内容会被判成「不是客户」。
                </p>
                <p className="font-semibold text-warning">
                  改过之后，「重新推断」不会再覆盖这一种的词。
                </p>
              </div>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {comment ? (
            // ★ 1009(契约 §5):评论线按话题词搜视频、再读评论 —— 「关键词」那张扁平表
            //   在这条线上不再是主入口,这里不摆它;保存时原样回传,不清掉(老 ai 仍读它兜底)。
            <>
              <ChipInput
                label="话题词（去哪类视频下找人）"
                hint="例如：商标注册、GEO优化 —— 你的业务名词，不是客户的问句"
                placeholder="回车加上"
                values={topicWords}
                onChange={setTopicWords}
                max={TOPIC_WORDS_MAX}
              />
              {topicOverflow ? (
                <p data-testid="topic-overflow" className="-mt-4 text-2xs text-destructive">
                  每轮只按前 {TOPIC_WORDS_MAX} 个搜，现在有 {topicWords.length} 个 —— 删到{" "}
                  {TOPIC_WORDS_MAX} 个才能保存
                </p>
              ) : topicWords.length === 0 ? (
                <p data-testid="topic-empty" className="-mt-4 text-2xs text-warning">
                  {topicCleared ? COMMENT_TOPIC_CLEARED_HINT : COMMENT_NO_TOPIC_HINT}
                </p>
              ) : (
                <p className="-mt-4 text-2xs text-muted-foreground">
                  最多 {TOPIC_WORDS_MAX} 个，每轮按这几个搜
                </p>
              )}
              <div className="space-y-2" data-testid="intent-patterns-section">
                <button
                  type="button"
                  onClick={() => setPatternsOpen((o) => !o)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  aria-expanded={patternsOpen}
                >
                  {patternsOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  客户常问（选填，只用于排序）
                  {!patternsOpen && intentPatterns.length > 0 && (
                    <span className="font-normal">· {intentPatterns.length} 条</span>
                  )}
                </button>
                {patternsOpen && (
                  <ChipInput
                    label="客户常问"
                    hint="例如：多少钱、怎么收费、能代办吗 —— 命中的评论排在前面，不影响搜哪些视频"
                    placeholder="回车加上"
                    values={intentPatterns}
                    onChange={setIntentPatterns}
                  />
                )}
              </div>
              {peers !== null && (
                <>
                  <div className="space-y-2" data-testid="peer-accounts-section">
                    <label htmlFor="peer-accounts" className="text-sm font-semibold">
                      竞品账号（去他们的{postNoun(item?.type)}下面找客户）
                    </label>
                    <p className="text-xs text-muted-foreground">
                      粘贴竞品的{platformName(item?.type)}主页链接或 App
                      里「分享主页」复制的内容，一行一个，最多 {PEER_ACCOUNTS_MAX} 个。
                    </p>
                    <Textarea
                      id="peer-accounts"
                      aria-label="竞品账号"
                      rows={3}
                      placeholder={
                        item?.type === "xhs_note"
                          ? "https://www.xiaohongshu.com/user/profile/…"
                          : "https://www.douyin.com/user/…  或  https://v.douyin.com/…"
                      }
                      value={peerAccountsText}
                      onChange={(e) => setPeerAccountsText(e.target.value)}
                    />
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>每个账号读最近评论最多的</span>
                      <select
                        aria-label="每个账号读几条"
                        className="rounded border border-input bg-background px-1 py-0.5 text-foreground"
                        value={peerPostsN}
                        onChange={(e) => setPeerPostsN(Number(e.target.value))}
                      >
                        {PEER_POSTS_N_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <span>条{postNoun(item?.type)}</span>
                    </div>
                    {peerAccounts.length > PEER_ACCOUNTS_MAX && (
                      <p className="text-xs text-destructive" data-testid="peer-accounts-overflow">
                        最多 {PEER_ACCOUNTS_MAX} 个，现在是 {peerAccounts.length} 个
                      </p>
                    )}
                  </div>
                  <div className="space-y-2" data-testid="peer-posts-section">
                    <label htmlFor="peer-posts" className="text-sm font-semibold">
                      指定{postNoun(item?.type)}（直接读这几条的评论区）
                    </label>
                    <p className="text-xs text-muted-foreground">
                      粘贴{postNoun(item?.type)}链接或 App 里「分享」复制的内容，一行一条，最多{" "}
                      {PEER_POSTS_MAX} 条。每轮优先读这些，读不完的下一轮接着读。
                    </p>
                    <Textarea
                      id="peer-posts"
                      aria-label={`指定${postNoun(item?.type)}`}
                      rows={3}
                      placeholder={
                        item?.type === "xhs_note"
                          ? "https://www.xiaohongshu.com/explore/…  或  http://xhslink.com/…"
                          : "https://www.douyin.com/video/…  或  https://v.douyin.com/…"
                      }
                      value={peerPostsText}
                      onChange={(e) => setPeerPostsText(e.target.value)}
                    />
                    {peerPosts.length > PEER_POSTS_MAX && (
                      <p className="text-xs text-destructive" data-testid="peer-posts-overflow">
                        最多 {PEER_POSTS_MAX} 条，现在是 {peerPosts.length} 条
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      这些来源出的客户卡会标「竞品评论区」。收费不变，仍是一轮一个价。
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <ChipInput
                label="关键词"
                hint="例如：客户会搜的「××怎么办」「××多少钱」"
                placeholder="回车加上"
                values={keywords}
                onChange={setKeywords}
              />
              {unrelated.length > 0 && (
                // 0925:0923 那次整批换成商标词、156 篇全判 0 —— 在加词这一刻就说,不等花一轮。
                <ul className="space-y-0.5" data-testid="keywords-unrelated-hint">
                  {unrelated.map((w) => (
                    <li key={w} className="text-2xs text-muted-foreground">
                      「{w}」{KEYWORD_IRRELEVANT_HINT}
                    </li>
                  ))}
                </ul>
              )}
              {removed.length > 0 && (
                // ★ 用户改过词的行,按产品分的词要与这张表求交(api AcqKeywordSelection 规矩 3):
                //   删掉的 AI 词,对应产品这条线以后也不搜了。0923 那次就是整批删掉后产品词全没了。
                <p className="text-xs text-warning" data-testid="keywords-removed-warning">
                  你删掉了 {removed.length} 个原有的词（{removed.slice(0, 3).join("、")}
                  {removed.length > 3 ? " 等" : ""}），保存后对应产品在这条线上也不会再搜它们。
                </p>
              )}
            </>
          )}
          <ChipInput
            label="地区"
            // 1021:评论线按评论者 IP 属地筛,平台只给到省 —— 照实写,别让本地生活的客户以为能筛同城。
            hint={
              !comment
                ? "留空 = 全国"
                : item?.type === "xigua_comment"
                  ? "留空 = 全国。西瓜视频不显示评论者属地，填了地区只保留评论里提到该地的"
                  : "留空 = 全国。按评论者 IP 属地筛，只能到省：填城市按所在省筛，评论里提到该地的也保留"
            }
            placeholder={comment ? "例如：浙江省、杭州" : "例如：浙江省"}
            values={regions}
            onChange={setRegions}
          />
        </div>

        <SheetFooter className="mt-8">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            取消
          </Button>
          <Button
            onClick={() =>
              comment
                ? onSave(keywords, regions, {
                    topicWords,
                    intentPatterns,
                    ...(peers !== null
                      ? { peerAccounts, peerPosts, peerAccountPostsN: peerPostsN }
                      : {}),
                  })
                : onSave(keywords, regions)
            }
            disabled={saving || peerOverflow || topicOverflow}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            保存
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
